import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2, X, ExternalLink } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { title: '', category: 'Energy Insights', excerpt: '', meta_description: '', keywords: '', cover_image: '', author: 'Scott H', content: '', published: true };

const Field = ({ label, hint, children }) => (
  <div style={{ marginBottom: '0.875rem' }}>
    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
      {label} {hint && <span style={{ fontWeight: 400, color: '#9CA3AF' }}>— {hint}</span>}
    </label>
    {children}
  </div>
);

export const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {..post} | EMPTY
  const [saving, setSaving] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/blog/posts`, { withCredentials: true });
      setPosts(res.data.posts || []);
    } catch (err) {
      toast.error('Failed to load blog posts');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleSave = async () => {
    if (!editing.title.trim() || !editing.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await axios.put(`${API}/api/admin/blog/posts/${editing.id}`, editing, { withCredentials: true });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/api/admin/blog/posts`, editing, { withCredentials: true });
        toast.success('Post published');
      }
      setEditing(null);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save post');
    }
    setSaving(false);
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/blog/posts/${post.id}`, { withCredentials: true });
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/api/admin/blog/posts/${post.id}`, { published: !post.published }, { withCredentials: true });
      toast.success(post.published ? 'Post unpublished' : 'Post published');
      fetchPosts();
    } catch (err) {
      toast.error('Failed to update post');
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><Loader2 size={28} className="animate-spin" style={{ color: 'var(--teal)' }} /></div>;
  }

  if (editing) {
    return (
      <div data-testid="blog-editor" style={{ maxWidth: 860 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)' }}>
            {editing.id ? 'Edit Post' : 'New Post'}
          </h2>
          <button onClick={() => setEditing(null)} data-testid="blog-editor-close" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280' }}>
            <X size={20} />
          </button>
        </div>

        <Field label="Title" hint="include your main keyword for SEO">
          <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} placeholder="e.g. How to Lower Your Business Electricity Bill" data-testid="blog-title-input" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
          <Field label="Category">
            <Input value={editing.category || ''} onChange={e => setEditing({ ...editing, category: e.target.value })} data-testid="blog-category-input" />
          </Field>
          <Field label="Author">
            <Input value={editing.author || ''} onChange={e => setEditing({ ...editing, author: e.target.value })} data-testid="blog-author-input" />
          </Field>
        </div>
        <Field label="Meta Description" hint="what Google shows in search results (max ~155 chars)">
          <Textarea rows={2} value={editing.meta_description || ''} onChange={e => setEditing({ ...editing, meta_description: e.target.value })} data-testid="blog-meta-input" />
        </Field>
        <Field label="Keywords" hint="comma-separated search terms">
          <Input value={editing.keywords || ''} onChange={e => setEditing({ ...editing, keywords: e.target.value })} placeholder="business electricity rates, energy savings" data-testid="blog-keywords-input" />
        </Field>
        <Field label="Excerpt" hint="short summary shown on the blog listing page">
          <Textarea rows={2} value={editing.excerpt || ''} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} data-testid="blog-excerpt-input" />
        </Field>
        <Field label="Cover Image URL" hint="optional">
          <Input value={editing.cover_image || ''} onChange={e => setEditing({ ...editing, cover_image: e.target.value })} placeholder="https://..." data-testid="blog-cover-input" />
        </Field>
        <Field label="Content" hint="Markdown supported: ## headings, **bold**, - lists, [links](/contact)">
          <Textarea rows={18} value={editing.content} onChange={e => setEditing({ ...editing, content: e.target.value })} style={{ fontFamily: 'monospace', fontSize: '0.82rem' }} data-testid="blog-content-input" />
        </Field>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '1rem' }}>
          <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="blog-save-btn">
            {saving ? <Loader2 size={16} className="animate-spin" /> : (editing.id ? 'Save Changes' : 'Publish Post')}
          </Button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={editing.published} onChange={e => setEditing({ ...editing, published: e.target.checked })} data-testid="blog-published-toggle" />
            Published
          </label>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)' }}>Blog Posts</h2>
          <p style={{ fontSize: '0.8rem', color: '#6B7280' }}>Manage the public blog at <a href="/blog" target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>/blog <ExternalLink size={10} style={{ display: 'inline' }} /></a></p>
        </div>
        <Button className="btn-primary" onClick={() => setEditing({ ...EMPTY })} data-testid="blog-new-post-btn" style={{ display: 'flex', gap: 6 }}>
          <Plus size={16} /> New Post
        </Button>
      </div>

      {posts.length === 0 ? (
        <p style={{ color: '#6B7280', padding: '2rem 0' }}>No posts yet. Create your first one.</p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
          {posts.map((post, i) => (
            <div key={post.id} data-testid={`blog-row-${post.slug}`} style={{
              display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.875rem 1rem',
              borderBottom: i < posts.length - 1 ? '1px solid #F3F4F6' : 'none',
            }}>
              {post.cover_image && (
                <img src={post.cover_image} alt="" style={{ width: 56, height: 38, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.title}</div>
                <div style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>
                  {post.category} · {post.read_time} min read · {new Date(post.published_at).toLocaleDateString()}
                </div>
              </div>
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 100, flexShrink: 0,
                background: post.published ? '#D1FAE5' : '#F3F4F6',
                color: post.published ? '#065F46' : '#6B7280',
              }}>{post.published ? 'Published' : 'Draft'}</span>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button onClick={() => togglePublish(post)} title={post.published ? 'Unpublish' : 'Publish'} data-testid={`blog-toggle-${post.slug}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', padding: 6 }}>
                  {post.published ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
                <button onClick={() => setEditing({ ...post })} title="Edit" data-testid={`blog-edit-${post.slug}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', padding: 6 }}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => handleDelete(post)} title="Delete" data-testid={`blog-delete-${post.slug}`} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#DC2626', padding: 6 }}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
